import Search from "@/src/sb/components/icon/Search"
import useOnOutsideClick from "@/src/sb/hooks/useOnOutsideClick"
import classNames from "classnames"
import {
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from "react"

import { BaseInputProps } from "./BaseInput"
import styles from "./search.module.css"

const SEARCH_INPUT_MODES = {
  gray: "bg-gray-100 border-stears_white focus-within:border-gray-400",
  white: "bg-stears-white border-gray-400 focus-within:border-stears_black",
  dark: "bg-gray-600 border-gray-800 focus-within:border-gray-800"
}
const SEARCH_ANIMATION = "transition-all ease-in-out duration-300"

export type SearchInputProps = Omit<
  BaseInputProps,
  | "type"
  | "leftReactNode"
  | "rightReactNode"
  | "extraInputClasses"
  | "label"
  | "errors"
> & {
  mode?: keyof typeof SEARCH_INPUT_MODES
  alwaysShowClearIcon?: boolean
  showClearIcon?: boolean
  searchButton?: boolean
  showLeftIcon?: boolean
  extraActionNode?: ReactNode
  hasBorder?: boolean
  hasShadow?: boolean
  collapsed?: boolean
  onSearchInputOpen?: () => void
  onSearchInputClose?: () => void
  onClick?: () => void
  fixedHeight?: boolean
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      collapsed = true,
      onSearchInputOpen,
      onSearchInputClose,
      fixedHeight = false,
      showClearIcon = true,
      ...props
    },
    ref
  ) => {
    const {
      onChange,
      ariaLabel,
      value,
      defaultValue,
      autoComplete,
      placeholder,
      name,
      id,
      alwaysShowClearIcon = false,
      mode = "gray",
      searchButton = false,
      extraActionNode = null,
      showLeftIcon = true,
      hasBorder = true,
      hasShadow = false,
      onClear,
      required,
      onKeyDown,
      readOnly,
      onClick
    } = props

    const [showSearchInput, setShowSearchInput] = useState(false)
    const shouldShowClearIcon =
      showClearIcon && (alwaysShowClearIcon || !!value)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const searchInputRootNode = useRef<HTMLInputElement | null>(null)

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    useOnOutsideClick(searchInputRootNode, () => {
      toggleShowSearchInput(false)
    })

    const handleClear = () => {
      inputRef.current?.focus()
      onClear?.()
    }

    const leftWidth = showLeftIcon ? "w-10 dark:w-4" : "w-4"

    const stylesCss = {
      white: styles.whiteinput,
      gray: styles.input,
      dark: styles.input
    }[mode]

    const hideInput = collapsed && !showSearchInput

    const toggleShowSearchInput = (searchInputState: boolean) => {
      setShowSearchInput(searchInputState)
      if (searchInputState) {
        inputRef.current?.focus()
        onSearchInputOpen?.()

        return
      }

      inputRef.current?.blur()
      onSearchInputClose?.()
    }

    return (
      <div
        ref={searchInputRootNode}
        className={classNames(
          `h-full rounded flex dark:rounded-none`,
          hideInput
            ? "bg-transparent dark:border-transparent"
            : "w-full dark:gap-2 dark:border-[#4B5563] dark:bg-[#1F2937] dark:px-2.5",
          {
            "border-[1px] dark:border-[0.5px]": hasBorder,
            "shadow-md": hasShadow,
            "min-h-[32px] md:min-h-[40px] dark:h-[32px] dark:md:h-[40px]":
              !fixedHeight
          },
          SEARCH_INPUT_MODES[mode],
          SEARCH_ANIMATION
        )}
      >
        <div
          className={`${leftWidth} h-full flex justify-center items-center shrink-0 self-center`}
        >
          {showLeftIcon && (
            <button
              className={
                "dark:inline-block text-stears_black dark:text-stears_white"
              }
              onClick={() => toggleShowSearchInput(!showSearchInput)}
            >
              {fixedHeight ? (
                <Search size={12} />
              ) : (
                <>
                  <span className={"hidden md:inline-block"}>
                    <Search />
                  </span>
                  <span className={"inline-block md:hidden"}>
                    <Search size={12} />
                  </span>
                </>
              )}
            </button>
          )}
        </div>

        <div
          className={classNames(
            "rounded-r-sm flex items-center",
            [hideInput ? "w-0" : "w-auto flex-grow"],
            SEARCH_ANIMATION
          )}
        >
          <input
            ref={inputRef}
            tabIndex={hideInput ? -1 : 0}
            type="search"
            className={`h-full dark:h-auto block pr-1 dark:font-jakarta outline-none bg-inherit w-full text-black dark:text-xs dark:placeholder:text-[#9CA3AF] dark:text-white ${stylesCss}`}
            onChange={onChange}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            value={value}
            readOnly={readOnly}
            defaultValue={defaultValue}
            placeholder={placeholder}
            autoComplete={autoComplete}
            id={id}
            name={name}
            required={required}
            onClick={onClick}
          />
        </div>

        {shouldShowClearIcon && !hideInput && (
          <div>
            <button
              className={`flex items-center justify-center h-full `}
              onClick={handleClear}
              aria-label="clear search"
              id={`clear-${id}`}
              type="button"
            >
              <span className="inline-flex items-center justify-center relative w-6 h-6">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 0.875L9.125 0L5 4.125L0.875 0L0 0.875L4.125 5L0 9.125L0.875 10L5 5.875L9.125 10L10 9.125L5.875 5L10 0.875Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </button>
          </div>
        )}

        {searchButton && !hideInput && (
          <div>
            <button
              className="px-3 flex items-center justify-center h-full"
              onClick={handleClear}
              aria-label="search articles"
              id={`search-${id}`}
            >
              <span className="flex items-center justify-center relative w-8 h-8">
                <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.75 16.6323L13.0312 11.8885C15.3746 9.06115 15.0822 4.89129 12.3672 2.4186C9.65219 -0.0540927 5.47324 0.0434461 2.87657 2.64012C0.2799 5.23679 0.182361 9.41574 2.65505 12.1307C5.12775 14.8457 9.2976 15.1381 12.125 12.7948L16.8687 17.5135L17.75 16.6323ZM2.125 7.51355C2.125 4.40694 4.6434 1.88855 7.75 1.88855C10.8566 1.88855 13.375 4.40694 13.375 7.51355C13.375 10.6201 10.8566 13.1385 7.75 13.1385C4.6434 13.1385 2.125 10.6201 2.125 7.51355Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </button>
          </div>
        )}

        {!hideInput ? extraActionNode : null}
      </div>
    )
  }
)
